import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Save,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Percent,
  Smartphone,
  Loader2,
  Mail,
  Check,
  X,
  AlertTriangle,
  FileText,
  Send,
  Calendar,
  Globe,
  Gift,
  Swords,
  Inbox,
} from "lucide-react";
import { Layout } from "../../layouts/DashboardLayout";
import useSystemSettings from "../../hooks/settings/useSystemSettings";
import useAdminEmails from "../../hooks/settings/useAdminEmails";
import { BACKEND_URL } from "../../utils/api";
import { ConcurrentsPanel } from "./ConcurrentsPage";

type SettingsTab = "system" | "admin-emails" | "withdrawal-email" | "monthly-reports" | "promo" | "concurrents";

const VALID_TABS: SettingsTab[] = [
  "system",
  "admin-emails",
  "withdrawal-email",
  "monthly-reports",
  "promo",
  "concurrents",
];

const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "system"
  );

  const selectTab = (tab: SettingsTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  useEffect(() => {
    const tab = searchParams.get("tab") as SettingsTab | null;
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const [actionLoading, setActionLoading] = useState(false);
  const [sendingReports, setSendingReports] = useState(false);
  const [encouragementMessage, setEncouragementMessage] = useState(
    "Merci pour votre confiance ! Continuez à utiliser IZI Life Pay pour vos transferts."
  );

  // Monthly Reports State
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);
  const [countries, setCountries] = useState<{ id: number; code: string; name: string; devise: string }[]>([]);
  const [selectedCountryIds, setSelectedCountryIds] = useState<number[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setCountriesLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BACKEND_URL}/country/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const json = await res.json();
          setCountries(json.data?.countries || []);
        }
      } catch (err) {
        console.error("Erreur chargement pays:", err);
      } finally {
        setCountriesLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const toggleCountry = (id: number) => {
    setSelectedCountryIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const selectAllCountries = () => {
    if (selectedCountryIds.length === countries.length) {
      setSelectedCountryIds([]);
    } else {
      setSelectedCountryIds(countries.map((c) => c.id));
    }
  };

  // Hooks
  const {
    settings: systemSettings,
    loading: settingsLoading,
    updateAppVersion,
    updateChargePercent,
    updateLocalOperatorNetworkError,
    updatePromoShareTemplate,
    updateAppDownloadUrl,
    updateWithdrawalNotificationEmail,
  } = useSystemSettings();

  const {
    emails: adminEmails,
    loading: emailsLoading,
    createEmail,
    updateEmail,
    updateStatus,
    deleteEmail,
  } = useAdminEmails();

  // System Settings Local State
  const [localSettings, setLocalSettings] = useState({
    app_version: "",
    charge_percent: "",
    local_operator_network_error: "",
    promo_share_template: "",
    app_download_url: "",
  });

  const [localWithdrawalEmail, setLocalWithdrawalEmail] = useState("");

  // Admin Emails State
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState({ name: "", email: "" });
  const [editingEmail, setEditingEmail] = useState<{ id: string; name: string; email: string } | null>(null);

  // Sync system settings when loaded
  useEffect(() => {
    if (systemSettings) {
      setLocalSettings({
        app_version: systemSettings.app_version || "",
        charge_percent: systemSettings.charge_percent || "",
        local_operator_network_error: systemSettings.local_operator_network_error || "",
        promo_share_template: systemSettings.promo_share_template || "",
        app_download_url: systemSettings.app_download_url || "",
      });
      setLocalWithdrawalEmail(systemSettings.withdrawal_notification_email || "");
    }
  }, [systemSettings]);

  // System Settings Handlers
  const handleSaveSystemSettings = async () => {
    setActionLoading(true);
    try {
      await Promise.all([
        updateAppVersion(localSettings.app_version),
        updateChargePercent(localSettings.charge_percent),
        updateLocalOperatorNetworkError(localSettings.local_operator_network_error),
      ]);
      alert("Paramètres système sauvegardés avec succès !");
    } catch (error) {
      alert("Erreur lors de la sauvegarde des paramètres");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveWithdrawalEmail = async () => {
    setActionLoading(true);
    try {
      await updateWithdrawalNotificationEmail(localWithdrawalEmail.trim() || null);
      alert("Email de notification de retrait sauvegardé avec succès !");
    } catch (error) {
      alert("Erreur lors de la sauvegarde de l'email");
    } finally {
      setActionLoading(false);
    }
  };

  // Promo Settings Handlers
  const handleSavePromoSettings = async () => {
    setActionLoading(true);
    try {
      await Promise.all([
        updatePromoShareTemplate(localSettings.promo_share_template),
        updateAppDownloadUrl(localSettings.app_download_url),
      ]);
      alert("Paramètres promotion sauvegardés avec succès !");
    } catch (error) {
      alert("Erreur lors de la sauvegarde des paramètres promotion");
    } finally {
      setActionLoading(false);
    }
  };

  // Show loading state
  if (settingsLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="mx-auto mb-4 w-12 h-12 text-green-600 animate-spin" />
            <p className="text-gray-600">Chargement des paramètres...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-800">Paramètres</h1>
        <p className="text-gray-600">Configuration des taux de change et paramètres système</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200">
        <div className="flex overflow-x-auto border-b border-gray-200">
          <button
            onClick={() => selectTab("withdrawal-email")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "withdrawal-email"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Inbox className="w-5 h-5" />
            Retraits
          </button>
          <button
            onClick={() => selectTab("system")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "system"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <SettingsIcon className="w-5 h-5" />
            Paramètres Système
          </button>
          <button
            onClick={() => selectTab("admin-emails")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "admin-emails"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Mail className="w-5 h-5" />
            Emails Admin
          </button>
          <button
            onClick={() => selectTab("monthly-reports")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "monthly-reports"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <FileText className="w-5 h-5" />
            Rapports Mensuels
          </button>
          <button
            onClick={() => selectTab("promo")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "promo" ? "text-green-600 border-b-2 border-green-600" : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Gift className="w-5 h-5" />
            Promotion
          </button>
          <button
            onClick={() => selectTab("concurrents")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "concurrents"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Swords className="w-5 h-5" />
            Concurrents
          </button>
        </div>
      </div>

      {/* Withdrawal Email Tab */}
      {activeTab === "withdrawal-email" && (
        <div className="p-6 space-y-6 bg-white rounded-lg border border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Email de notification des retraits</h2>
            <p className="text-sm text-gray-600">
              Configurez l'adresse email qui recevra les notifications des demandes de retrait des utilisateurs.
            </p>
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Adresse email</label>
            <input
              type="email"
              value={localWithdrawalEmail}
              onChange={(e) => setLocalWithdrawalEmail(e.target.value)}
              placeholder="admin@izilifepay.com"
              className="px-4 py-2 w-full max-w-md rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <button
              onClick={handleSaveWithdrawalEmail}
              disabled={actionLoading}
              className="flex gap-2 items-center px-6 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <Save className="w-4 h-4" />
              Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Admin Emails Tab */}
      {activeTab === "admin-emails" && (
        <div className="space-y-6">
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Emails Administrateurs</h2>
                <p className="text-sm text-gray-600">Gérer les emails qui reçoivent les notifications KYC</p>
              </div>
              <button
                onClick={() => setShowAddEmailModal(true)}
                className="flex gap-2 items-center px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                Ajouter un Email
              </button>
            </div>

            {/* Emails List */}
            <div className="space-y-3">
              {adminEmails.map((email) => (
                <div
                  key={email.id}
                  className="flex justify-between items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <div className="flex gap-4 items-center">
                    <div className="flex justify-center items-center w-12 h-12 bg-blue-100 rounded-full">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{email.name || "Sans nom"}</p>
                      <p className="text-sm text-gray-600">{email.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        email.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {email.status}
                    </span>
                    <button
                      onClick={() => {
                        setEditingEmail({ id: email.id, name: email.name || "", email: email.email });
                        setShowEditEmailModal(true);
                      }}
                      disabled={actionLoading}
                      className="p-2 text-gray-400 transition-colors hover:text-blue-600 disabled:opacity-50"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async () => {
                        setActionLoading(true);
                        try {
                          await updateStatus(email.id, email.status === "ACTIVE" ? "INACTIVE" : "ACTIVE");
                          alert("Statut mis à jour avec succès !");
                        } catch (error) {
                          alert("Erreur lors de la mise à jour du statut");
                        } finally {
                          setActionLoading(false);
                        }
                      }}
                      disabled={actionLoading}
                      className="p-2 text-gray-400 transition-colors hover:text-orange-600 disabled:opacity-50"
                      title="Changer le statut"
                    >
                      <RefreshCw className={`w-4 h-4 ${actionLoading ? "animate-spin" : ""}`} />
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm(`Êtes-vous sûr de vouloir supprimer ${email.email} ?`)) {
                          setActionLoading(true);
                          try {
                            await deleteEmail(email.id);
                            alert("Email supprimé avec succès !");
                          } catch (error) {
                            alert("Erreur lors de la suppression");
                          } finally {
                            setActionLoading(false);
                          }
                        }
                      }}
                      disabled={actionLoading}
                      className="p-2 text-gray-400 transition-colors hover:text-red-600 disabled:opacity-50"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add Email Modal */}
          {showAddEmailModal && (
            <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
              <div className="p-6 w-full max-w-md bg-white rounded-lg">
                <h3 className="mb-4 text-xl font-bold text-gray-800">Ajouter un Email Admin</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Nom (optionnel)</label>
                    <input
                      type="text"
                      placeholder="Admin Principal"
                      value={newEmail.name}
                      onChange={(e) => setNewEmail({ ...newEmail, name: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      placeholder="admin@izilifepay.com"
                      value={newEmail.email}
                      onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={async () => {
                      if (!newEmail.email) {
                        alert("Veuillez entrer un email");
                        return;
                      }
                      setActionLoading(true);
                      try {
                        await createEmail(newEmail.name, newEmail.email);
                        setNewEmail({ name: "", email: "" });
                        setShowAddEmailModal(false);
                        alert("Email ajouté avec succès !");
                      } catch (error) {
                        alert("Erreur lors de l'ajout de l'email");
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    disabled={actionLoading}
                    className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Ajouter
                  </button>
                  <button
                    onClick={() => setShowAddEmailModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Email Modal */}
          {showEditEmailModal && editingEmail && (
            <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
              <div className="p-6 w-full max-w-md bg-white rounded-lg">
                <h3 className="mb-4 text-xl font-bold text-gray-800">Modifier l'Email Admin</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Nom</label>
                    <input
                      type="text"
                      value={editingEmail.name}
                      onChange={(e) => setEditingEmail({ ...editingEmail, name: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={editingEmail.email}
                      onChange={(e) => setEditingEmail({ ...editingEmail, email: e.target.value })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={async () => {
                      setActionLoading(true);
                      try {
                        await updateEmail(editingEmail.id, editingEmail.name, editingEmail.email);
                        setShowEditEmailModal(false);
                        setEditingEmail(null);
                        alert("Email modifié avec succès !");
                      } catch (error) {
                        alert("Erreur lors de la modification");
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    disabled={actionLoading}
                    className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Modifier
                  </button>
                  <button
                    onClick={() => {
                      setShowEditEmailModal(false);
                      setEditingEmail(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Settings Tab */}
      {activeTab === "system" && (
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800">Paramètres Système</h2>
            <p className="text-sm text-gray-600">Configuration des limites et paramètres de l'application</p>
          </div>

          <div className="space-y-6">
            {/* App Version */}
            <div className="flex gap-4 items-center">
              <div className="flex justify-center items-center w-12 h-12 bg-blue-100 rounded-lg">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <label className="block mb-1 text-sm font-medium text-gray-700">Version de l'Application</label>
                <input
                  type="text"
                  value={localSettings.app_version}
                  onChange={(e) => setLocalSettings({ ...localSettings, app_version: e.target.value })}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="1.0.0"
                />
              </div>
            </div>

            {/* Charge Percent */}
            <div className="flex gap-4 items-center">
              <div className="flex justify-center items-center w-12 h-12 bg-purple-100 rounded-lg">
                <Percent className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <label className="block mb-1 text-sm font-medium text-gray-700">Pourcentage de Frais (%)</label>
                <input
                  type="number"
                  value={localSettings.charge_percent}
                  onChange={(e) => setLocalSettings({ ...localSettings, charge_percent: e.target.value })}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="5"
                  step="0.1"
                />
                <p className="mt-1 text-xs text-gray-500">Pourcentage de frais appliqué sur chaque transaction</p>
              </div>
            </div>

            {/* Local Operator Network Error */}
            <div className="flex gap-4 items-center">
              <div className="flex justify-center items-center w-12 h-12 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <label className="block mb-1 text-sm font-medium text-gray-700">Erreur Réseau Opérateur Local</label>
                <select
                  value={localSettings.local_operator_network_error}
                  onChange={(e) => setLocalSettings({ ...localSettings, local_operator_network_error: e.target.value })}
                  className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="NO">NON</option>
                  <option value="YES">OUI</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Indique si l'opérateur local rencontre des problèmes réseau
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleSaveSystemSettings}
                disabled={actionLoading}
                className="flex gap-2 justify-center items-center px-4 py-3 w-full text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Enregistrer les Paramètres
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Reports Tab */}
      {activeTab === "monthly-reports" && (
        <div className="space-y-6">
          {/* Header */}
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Rapports Mensuels</h2>
                <p className="text-sm text-gray-600">
                  Envoyer un récapitulatif PDF des transactions réussies par période et zone géographique
                </p>
              </div>
              <FileText className="w-8 h-8 text-green-600" />
            </div>

            {/* Info Box */}
            <div className="p-4 mb-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Information :</strong> Sélectionnez une période et les zones géographiques souhaitées. Un
                    email avec un rapport PDF sera envoyé à chaque utilisateur ayant au moins une transaction réussie.
                  </p>
                  <p className="mt-1 text-xs text-blue-600">
                    Le traitement se fait en arrière-plan et peut prendre plusieurs minutes.
                  </p>
                </div>
              </div>
            </div>

            {/* ── Sélection de la période ── */}
            <div className="p-5 mb-6 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex gap-2 items-center mb-4">
                <Calendar className="w-5 h-5 text-green-600" />
                <h3 className="text-sm font-semibold text-gray-800">Période du rapport</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-600">Date de début</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-4 py-2.5 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-600">Date de fin</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-4 py-2.5 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Période sélectionnée : du <strong>{new Date(startDate).toLocaleDateString("fr-FR")}</strong> au{" "}
                <strong>{new Date(endDate).toLocaleDateString("fr-FR")}</strong>
              </p>
            </div>

            {/* ── Sélection des zones géographiques ── */}
            <div className="p-5 mb-6 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex gap-2 justify-between items-center mb-4">
                <div className="flex gap-2 items-center">
                  <Globe className="w-5 h-5 text-green-600" />
                  <h3 className="text-sm font-semibold text-gray-800">Zones géographiques</h3>
                </div>
                <button
                  onClick={selectAllCountries}
                  className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full transition-colors hover:bg-green-200"
                >
                  {selectedCountryIds.length === countries.length ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
              </div>

              {countriesLoading ? (
                <div className="flex justify-center items-center py-4">
                  <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                  <span className="ml-2 text-sm text-gray-500">Chargement des pays...</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                  {countries.map((country) => (
                    <button
                      key={country.id}
                      onClick={() => toggleCountry(country.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg border transition-all ${
                        selectedCountryIds.includes(country.id)
                          ? "bg-green-50 border-green-400 text-green-800 shadow-sm"
                          : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded flex items-center justify-center border ${
                          selectedCountryIds.includes(country.id) ? "bg-green-500 border-green-500" : "border-gray-300"
                        }`}
                      >
                        {selectedCountryIds.includes(country.id) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-medium">{country.name}</span>
                      {country.devise && <span className="text-xs text-gray-400">({country.devise})</span>}
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-gray-500">
                {selectedCountryIds.length === 0
                  ? "Aucun filtre — le rapport sera envoyé à tous les utilisateurs."
                  : `${selectedCountryIds.length} zone(s) sélectionnée(s).`}
              </p>
            </div>

            {/* ── Message d'encouragement ── */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Message d'encouragement et de remerciement
              </label>
              <textarea
                value={encouragementMessage}
                onChange={(e) => setEncouragementMessage(e.target.value)}
                rows={4}
                placeholder="Entrez un message d'encouragement pour vos utilisateurs..."
                className="px-4 py-3 w-full rounded-lg border border-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-2 text-xs text-gray-500">
                Ce message sera inclus dans l'email envoyé aux utilisateurs avec leur rapport.
              </p>
            </div>

            {/* Aperçu du message */}
            <div className="p-4 mb-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <p className="mb-2 text-xs font-semibold text-green-700">APERÇU DU MESSAGE</p>
              <p className="text-sm italic text-gray-800">"{encouragementMessage}"</p>
            </div>

            {/* Résumé */}
            <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-green-600">Période</p>
                    <p className="text-lg font-bold text-green-800">
                      {new Date(startDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                      {" — "}
                      {new Date(endDate).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-green-600 opacity-50" />
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-blue-600">Zones</p>
                    <p className="text-lg font-bold text-blue-800">
                      {selectedCountryIds.length === 0 ? "Toutes" : `${selectedCountryIds.length} pays`}
                    </p>
                    <p className="mt-1 text-xs text-blue-600">
                      {selectedCountryIds.length === 0
                        ? "Aucun filtre géographique"
                        : countries
                            .filter((c) => selectedCountryIds.includes(c.id))
                            .map((c) => c.code)
                            .join(", ")}
                    </p>
                  </div>
                  <Globe className="w-8 h-8 text-blue-600 opacity-50" />
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-purple-600">Format</p>
                    <p className="text-lg font-bold text-purple-800">PDF + Email</p>
                    <p className="mt-1 text-xs text-purple-600">Avec message personnalisé</p>
                  </div>
                  <Mail className="w-8 h-8 text-purple-600 opacity-50" />
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                onClick={async () => {
                  if (!encouragementMessage.trim()) {
                    alert("Veuillez entrer un message d'encouragement");
                    return;
                  }

                  if (!startDate || !endDate) {
                    alert("Veuillez sélectionner une période valide");
                    return;
                  }

                  const zoneLabel =
                    selectedCountryIds.length === 0
                      ? "tous les utilisateurs"
                      : `les utilisateurs de ${selectedCountryIds.length} zone(s)`;

                  if (
                    !confirm(
                      `Envoyer les rapports du ${new Date(startDate).toLocaleDateString("fr-FR")} au ${new Date(endDate).toLocaleDateString("fr-FR")} à ${zoneLabel} ?\n\nCette action ne peut pas être annulée.`
                    )
                  ) {
                    return;
                  }

                  setSendingReports(true);
                  try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${BACKEND_URL}/user/send-monthly-reports`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({
                        encouragement_message: encouragementMessage,
                        start_date: startDate,
                        end_date: endDate,
                        country_ids: selectedCountryIds.length > 0 ? selectedCountryIds : [],
                      }),
                    });

                    if (!response.ok) {
                      throw new Error("Erreur lors de l'envoi des rapports");
                    }

                    const data = await response.json();
                    alert(
                      `Envoi des rapports lancé avec succès !\n\n${data.message || "Les rapports seront envoyés en arrière-plan."}`
                    );
                  } catch (error) {
                    console.error("Erreur:", error);
                    alert("Erreur lors de l'envoi des rapports. Veuillez réessayer.");
                  } finally {
                    setSendingReports(false);
                  }
                }}
                disabled={sendingReports || !encouragementMessage.trim()}
                className="flex gap-3 justify-center items-center px-6 py-4 w-full font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg shadow-lg transition-all hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl"
              >
                {sendingReports ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer les Rapports
                  </>
                )}
              </button>

              {sendingReports && (
                <div className="p-4 mt-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start">
                    <Loader2 className="w-5 h-5 text-yellow-600 animate-spin mt-0.5 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Traitement en cours...</p>
                      <p className="mt-1 text-xs text-yellow-700">
                        Les rapports sont en cours de génération et d'envoi. Vous pouvez fermer cette page, le processus
                        continuera en arrière-plan.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Help Section */}
            <div className="p-4 mt-6 bg-gray-50 rounded-lg">
              <h3 className="mb-2 text-sm font-semibold text-gray-800">Contenu du rapport PDF</h3>
              <ul className="space-y-1 text-xs text-gray-600">
                <li>- Logo IZI Life Pay</li>
                <li>- Nom complet de l'utilisateur</li>
                <li>- Période du rapport sélectionnée</li>
                <li>- Liste des transactions réussies uniquement</li>
                <li>- Montants totaux envoyés (CAD et FCFA)</li>
                <li>- Nombre total de transactions</li>
                <li>- Message d'encouragement et de remerciement personnalisé</li>
                <li>- Date de génération du rapport</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Promotion Tab */}
      {activeTab === "promo" && (
        <div className="space-y-6">
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Configuration Promotion</h2>
                <p className="text-sm text-gray-600">Personnalisez le message de partage des codes promo</p>
              </div>
              <button
                onClick={handleSavePromoSettings}
                disabled={actionLoading}
                className="flex gap-2 items-center px-6 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Sauvegarder
              </button>
            </div>

            {/* URL de téléchargement */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                URL de téléchargement de l'application
              </label>
              <input
                type="url"
                value={localSettings.app_download_url}
                onChange={(e) =>
                  setLocalSettings((prev) => ({
                    ...prev,
                    app_download_url: e.target.value,
                  }))
                }
                placeholder="https://izilifepay.com/download"
                className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-1 text-xs text-gray-500">Lien utilisé comme fallback si AppsFlyer OneLink échoue.</p>
            </div>

            {/* Template du message */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-semibold text-gray-700">Message de partage</label>
              <textarea
                value={localSettings.promo_share_template}
                onChange={(e) =>
                  setLocalSettings((prev) => ({
                    ...prev,
                    promo_share_template: e.target.value,
                  }))
                }
                rows={5}
                placeholder="Télécharge L'application IZI LIFE PAY et reçoit 5$ gratuit..."
                className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-gray-500">Variables disponibles :</span>
                <span className="px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded">{"{code}"}</span>
                <span className="px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded">{"{link}"}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Placez {"{code}"} et {"{link}"} à n'importe quel endroit dans le message. Ils seront remplacés
                automatiquement.
              </p>
            </div>

            {/* Preview */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="mb-3 text-sm font-semibold text-gray-700">Aperçu du message</h3>
              <div className="p-4 bg-white rounded-lg border border-gray-200 whitespace-pre-wrap text-gray-800 text-sm leading-relaxed">
                {localSettings.promo_share_template
                  .replace(/{code}/g, "IZI-JD-A3B7C9")
                  .replace(/{link}/g, "https://izilifepay.onelink.me/abc123?ref=IZI-JD-A3B7C9")}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "concurrents" && <ConcurrentsPanel />}
    </Layout>
  );
};

export default SettingsPage;
